import React, { useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useSubscribeAll } from '@atlaskit/rovo-triggers/main';
import { getRovoParams } from '@atlaskit/rovo-triggers/params';
import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';
import { RovoPostMessagePubsubListener } from '@atlaskit/rovo-triggers/post-message-to-pubsub';
import { type Payload } from '@atlaskit/rovo-triggers/types';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

type Props = {
	onTriggerOpeningChat: (
		conversationId?: string,
		chatOpenMode?: RovoChatOpenMode,
		source?: string,
	) => void;
};

// TODO: Deprecated; teams integrating this event should opt-out of chat open using `openChat` on the core event payload
// Once all teams have opted out the ignoredEvents logic can be removed
const ignoredEvents = new Set<Payload['type']>([
	'editor-context-payload',
	'browser-context-payload',
	'agent-changed',
]);

const eventsToSkipAnalytics = new Set<Payload['type']>([
	// This event is fired way too often https://atlassian.slack.com/archives/C06PKQPN8BF/p1729569226011399
	'editor-context-payload',
	'agent-changed',
]);

/**
 * This component is meant to be used at root level of the app
 * Outside chat sidebar, but under the assumption that the chat sidebar exists in that app
 *
 * 1. This component listens to all pubsub events emitted
 * for specific events, it will call `onEventTriggersChatOpen`
 *
 * 2. It's also used to subscribe to `rovoChatTriggerOpen` params!
 *
 * 3. Then it also handles `RovoPostMessagePubsubListener` to listen to postMessage events targeted for chat sidebar
 *
 * Example use case: if Rovo chat is closed in Confluence sidebar
 * and there is a reading-aids follow up triggering 'chat-new' pubsub event
 * We want to open the chat, and create a new conversation following that
 *
 * This component will be responsible to handle opening the chat, by calling `onEventTriggersChatOpen`
 * Each product will pass their own callback on how to open chat
 */
export const ChatOpenerSubscriber = ({ onTriggerOpeningChat }: Props): React.JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();

	const rovoParams = useMemo(() => getRovoParams(), []);
	const [triggerOpen, setTriggerOpen] = useState<boolean>(!!rovoParams?.triggerOpen);

	// Trigger open rovo chat via URL param, respecting openChatMode if provided
	useEffect(() => {
		try {
			if (!triggerOpen) {
				return;
			}
			if (fg('rovo_chat_openchatmode_query_param')) {
				onTriggerOpeningChat(rovoParams?.conversationId, rovoParams?.openChatMode);
			} else {
				onTriggerOpeningChat();
			}
			setTriggerOpen(false);
		} catch (error) {
			// Fail silently
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerOpen]);

	useSubscribeAll((payload) => {
		const { type, source, openChat = true } = payload;
		const preventOpen = !openChat || ignoredEvents.has(type);

		if (!eventsToSkipAnalytics.has(type)) {
			sendAnalyticsEvent({
				action: 'eventReceived',
				actionSubject: 'aiMatePubSubEvent',
				source,
				attributes: {
					publisherSource: source,
					payloadType: type,
					eventTriggersChatOpen: !preventOpen,
				},
			});
		}

		if (preventOpen) {
			return;
		}

		// Extract conversation ID from chat-open events
		const conversationId =
			type === 'chat-open' && 'data' in payload ? payload.data.channelId : undefined;

		const openChatMode = 'openChatMode' in payload ? payload.openChatMode : undefined;

		if (fg('fix-jira-ai-plugin-dropdown-overlap')) {
			onTriggerOpeningChat(conversationId, openChatMode, source);
		} else {
			onTriggerOpeningChat(conversationId, openChatMode);
		}
	});

	return (
		<>
			<RovoPostMessagePubsubListener />
		</>
	);
};
